
import { useState } from 'react';

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    employees: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Form submission disabled - replace with your own endpoint
      console.log('Contact form submitted:', formData);
      setSubmitStatus('Message logged to console. Replace with your own form handler.');
      setFormData({ name: '', email: '', company: '', employees: '', message: '' });
    } catch (error) {
      setSubmitStatus('Failed to send message. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-black to-gray-900">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Ready to <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Secure</span> Your Business?
              </h2>
              <p className="text-gray-400 text-xl leading-relaxed mb-8">
                Get a personalized demo and see how Driscop can protect your UK business from phishing and smishing attacks.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/200/20 rounded-xl flex items-center justify-center">
                  <i className="ri-map-pin-line text-2xl text-blue-400"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Manchester, UK</h4>
                  <p className="text-gray-400">Proudly serving UK businesses</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/200/20 rounded-xl flex items-center justify-center">
                  <i className="ri-mail-line text-2xl text-blue-400"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold">hello@driscop.co.uk</h4>
                  <p className="text-gray-400">Get in touch with our team</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/200/20 rounded-xl flex items-center justify-center">
                  <i className="ri-time-line text-2xl text-blue-400"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Response Time</h4>
                  <p className="text-gray-400">Within 24 hours</p>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
              <h4 className="text-white font-semibold mb-4">Why Businesses Trust Driscop</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-gray-300">UK-based support</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-gray-300">GDPR compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-gray-300">14-day free trial</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-gray-300">No setup fees</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Get Your Free Demo</h3>
              <p className="text-gray-400">See how Driscop protects your business in real-time</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Business Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="company"
                  placeholder="Company Name"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors duration-300"
                />
                <div className="relative">
                  <select
                    name="employees"
                    value={formData.employees}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 pr-8 text-white focus:border-blue-500 focus:outline-none transition-colors duration-300 appearance-none"
                  >
                    <option value="" className="bg-gray-800">Number of Employees</option>
                    <option value="1-10" className="bg-gray-800">1-10 employees</option>
                    <option value="11-50" className="bg-gray-800">11-50 employees</option>
                    <option value="51-200" className="bg-gray-800">51-200 employees</option>
                    <option value="200+" className="bg-gray-800">200+ employees</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>
              
              <textarea
                name="message"
                placeholder="Tell us about your cybersecurity challenges..."
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={4}
                className="w-full bg-transparent border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors duration-300 resize-none"
              ></textarea>
              <div className="text-right text-gray-500 text-sm">
                {formData.message.length}/500
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Book Your Free Demo'}
              </button>
              
              {submitStatus && (
                <div className={`text-center py-2 ${submitStatus.includes('successfully') ? 'text-green-400' : 'text-red-400'}`}>
                  {submitStatus}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
